import type { ComboBoxFilterChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import {
	DropDownList,
	MultiSelect,
} from "@tql/ui-components-react/tql/dropdowns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import type { z } from "zod";

import type { Customer, LtlOwner } from "~/shared/schemas/customer-schema.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

import styles from "./user-filter-multiselect.module.css";

type FilterItem = {
	id: string | number;
	name: string;
};

type UserFilter = {
	id: number;
	name: string;
	endpoint: string;
	schema: z.ZodSchema<unknown> | null;
	paramType: string;
};

type UserFilterMultiselectProps = {
	onHandleChange: (e: { value: FilterItem[] }) => void;
	onHandleSelectFilter: ({ value }: { value: UserFilter }) => void;
	selectedFilter: UserFilter | undefined;
	selectedValue: FilterItem[];
	userFilters: UserFilter[];
	showLabel?: boolean;
};

function renderItem(
	li: React.ReactElement,
	itemProps: { dataItem: FilterItem; selected: boolean },
) {
	return (
		<li className={itemProps.selected ? "k-item k-state-selected" : "k-item"}>
			<span>{itemProps.dataItem.name}</span>
		</li>
	);
}

export function UserFilterMultiselect({
	onHandleChange,
	onHandleSelectFilter,
	selectedFilter,
	selectedValue,
	userFilters,
	showLabel = true,
}: Readonly<UserFilterMultiselectProps>) {
	const [data, setData] = useState<FilterItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		setData([]);
		setSearchTerm("");
	}, [selectedFilter]);

	const fetchResults = useCallback(
		async (term: string) => {
			if (term.length < 3) {
				return;
			}

			// Abort any previous request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			setIsLoading(true);
			try {
				const baseRoute = selectedFilter?.endpoint;
				const schema = selectedFilter?.schema;
				if (!baseRoute || !schema) {
					return;
				}
				const results = await getAndValidate(
					baseRoute.replace(":userInput", term),
					schema,
					{ signal: abortController.signal },
				);
				if (results.success && Array.isArray(results.data)) {
					const formattedResults: FilterItem[] = results.data.map(
						(item: Customer | LtlOwner) => ({
							id: "id" in item ? item.id : item.userId,
							name: item.name,
						}),
					);
					setData(formattedResults);
				} else {
					console.error("Error fetching search results:", results);
				}
			} catch (error) {
				// Don't log abort errors as they are expected
				if (error instanceof Error && error.name === "AbortError") {
					return;
				}
				console.error("Error fetching search results:", error);
			} finally {
				setIsLoading(false);
			}
		},
		[selectedFilter],
	);

	useEffect(() => {
		void fetchResults(debouncedSearchTerm);
	}, [debouncedSearchTerm, fetchResults]);

	function handleFilterChange(e: ComboBoxFilterChangeEvent) {
		const value = (e.filter.value as string) || "";
		setSearchTerm(value);
	}

	return (
		<div
			className={styles.userFilterMultiselect}
			data-testid={"quote-board-filter-value"}
		>
			<div
				className={styles.userSelect}
				data-testid={"quote-board-filter-type"}
			>
				{showLabel ? "User" : null}
				<DropDownList
					ariaLabel={"Filter type"}
					data={userFilters}
					dataItemKey={"id"}
					onChange={onHandleSelectFilter}
					style={{ width: "150px" }}
					textField={"name"}
					value={
						selectedFilter ?? {
							id: 0,
							name: "Select Filter",
							endpoint: "",
							schema: null,
						}
					}
				/>
			</div>

			<MultiSelect
				ariaLabel={"Filter value"}
				data={data}
				dataItemKey={"id"}
				disabled={!selectedFilter}
				filterable={true}
				id={"quote-board-filter-value-input"}
				itemRender={renderItem}
				loading={isLoading}
				noDataText={"No Results Found"}
				onChange={onHandleChange}
				onFilterChange={handleFilterChange}
				style={{
					minWidth: 200,
					width: "fit-content",
					maxWidth: "100%",
					flex: "0 0 auto",
				}}
				tagLimit={5}
				textField={"name"}
				value={selectedValue}
			/>
		</div>
	);
}
